import type { AnySceneObjectT } from "tweenly"
import { ObjectsStateT } from "../store/slices/objects.slice"

/**
 * Does anything in this subtree carry an animation?
 */
const subtreeHasAnimations = (
    object: AnySceneObjectT,
    objectsById: Record<string, AnySceneObjectT>
): boolean => {
    if (object.animations && Object.keys(object.animations).length > 0) return true

    return object.childIds.some((childId) => {
        const child = objectsById[childId]
        return child ? subtreeHasAnimations(child, objectsById) : false
    })
}

/**
 * Does any ancestor of this object carry an animation?
 *
 * Geometry flows both ways: an auto-frame sizes itself from its children, so editing a child
 * changes the frame's measured box. If the frame has a tween, that tween recorded its start
 * values from the pre-edit DOM and has to be rebuilt — even though the change happened
 * strictly below it.
 */
const ancestorHasAnimations = (
    object: AnySceneObjectT,
    objectsById: Record<string, AnySceneObjectT>
): boolean => {
    let parent = object.parentId ? objectsById[object.parentId] : undefined
    const seen = new Set<string>()

    while (parent && !seen.has(parent.id)) {
        seen.add(parent.id)
        if (parent.animations && Object.keys(parent.animations).length > 0) return true
        parent = parent.parentId ? objectsById[parent.parentId] : undefined
    }

    return false
}

/**
 * A `position: static` object is laid out by a flex parent, so changing its geometry moves
 * its siblings too — and their tweens record start values from the DOM.
 */
const isFlexChild = (object: AnySceneObjectT): boolean =>
    object.styles.find((style) => style.property === "position")?.value === "static"

/**
 * Can the master timeline rebuild be skipped for this change?
 *
 * `updateMasterTimeline` kills and recreates every tween of the scene and rewrites the inline
 * styles of every object. Editing one property of one object — a colour, a text, a CSS value —
 * used to pay that price in full, because `updatedAt` says only THAT something changed, never
 * WHAT.
 *
 * The rebuild can be skipped only when the change provably cannot invalidate any tween:
 *
 * - the scope has to be known (`changedIds !== null`; see `markObjectsUpdated`)
 * - neither the changed object nor any of its descendants may have an animation — a parent's
 *   geometry feeds its children's layout, which is why descendants count
 * - the object must not be a flex child, because then its geometry also moves its siblings,
 *   whose tweens would then record stale start values
 *
 * Anything else falls back to the full rebuild, which is the old behaviour.
 */
export const canSkipMasterTimelineRebuild = (
    objects: ObjectsStateT,
    changedIds: string[] | null
): boolean => {
    if (changedIds === null || changedIds.length === 0) return false

    return changedIds.every((id) => {
        const object = objects.value[id]
        if (!object) return false
        if (isFlexChild(object)) return false
        if (ancestorHasAnimations(object, objects.value)) return false

        return !subtreeHasAnimations(object, objects.value)
    })
}

/**
 * The object itself plus every descendant. A parent's geometry feeds its children's layout,
 * so a targeted update always has to cover the whole subtree.
 */
export const collectSubtreeIds = (
    objectsById: Record<string, AnySceneObjectT>,
    rootIds: string[]
): string[] => {
    const collected = new Set<string>()

    const walk = (id: string) => {
        if (collected.has(id)) return
        const object = objectsById[id]
        if (!object) return
        collected.add(id)
        object.childIds.forEach(walk)
    }

    rootIds.forEach(walk)
    return Array.from(collected)
}

/**
 * Can this change be handled by replacing the tweens of one subtree, instead of rebuilding
 * the whole master timeline?
 *
 * Everything the full rebuild does that is NOT per-object rules it out:
 *
 * - `buildSequenceDescriptors` / `configureSequencePlayer` are derived from ALL animations,
 *   and sequence animations also emit `data-tw-seq` markers — so any sequence animation in
 *   the subtree forces the full path
 * - ticker sub-timelines (`subTimeline === true`) have their own machinery, measure the DOM
 *   and dispatch `initSubTimelineAction`
 * - a flex child moves its siblings when its geometry changes, and their tweens record start
 *   values from the DOM, so the subtree is not the real scope
 * - an animated ancestor (an auto-frame sizes itself from its children) is outside the
 *   subtree, so its tween would keep start values recorded before the change
 */
export const canUpdateTweensInPlace = (objects: ObjectsStateT, subtreeIds: string[]): boolean => {
    if (subtreeIds.length === 0) return false

    const inSubtree = new Set(subtreeIds)

    return subtreeIds.every((id) => {
        const object = objects.value[id]
        if (!object) return false
        if (isFlexChild(object)) return false
        // Only ancestors OUTSIDE the subtree matter — the ones inside are rebuilt here
        // anyway, and they are usually the very object that changed.
        if (
            !(object.parentId && inSubtree.has(object.parentId)) &&
            ancestorHasAnimations(object, objects.value)
        ) {
            return false
        }

        return Object.values(object.animations ?? {}).every(
            (animation) =>
                animation && !animation.subTimeline && animation.tween.name !== "sequence"
        )
    })
}
